import { useParams, useSearchParams } from 'react-router-dom';
import { useGetPolicy, useSearchPolicyTransactions } from '@services/policy';
import { useQuote } from '@services/quotes';
import { searchLastSuccessfulRatingParams } from '@services/quoteRating/quoteRating.helpers';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import {
  ModalType,
  getQuoteSummaryData,
  QuoteSummaryData,
} from '@pages/QuoteDetails/sidebarItems/summary/getQuoteSummaryData';
import type {
  PolicyGetResponse,
  PolicyTransactionSearchResponse,
  QuoteRatingSearchResponse,
  QuoteGetResponse,
} from '@lio/api-types';
import { StructuredQuote } from '@store/Quote/Quote.types';
import { QuoteType } from '@models/quotes';
import { InfiniteData } from '@tanstack/react-query';
import { SEARCH_PARAMS } from '@pages/QuoteDetails/sidebarItems/summary/Summary';
import invariant from 'tiny-invariant';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export interface UseQuoteSummaryHelperReturn {
  quoteId: string;
  quote: QuoteGetResponse | undefined;
  structuredQuote: StructuredQuote | undefined;
  policy: PolicyGetResponse | undefined;
  lastSuccessfulRating: InfiniteData<QuoteRatingSearchResponse> | undefined;
  policyTransactions: PolicyTransactionSearchResponse | undefined;
  isInternalUser: boolean;
  isRatingModalOpen: boolean;
  isTIVModalOpen: boolean;
  isKYTaxModalOpen: boolean;
  toggleModalOpen: (type: ModalType, isModalOpen: boolean) => void;
  setModalOpen: (type: ModalType, isOpen: boolean) => void;
  summaryData: QuoteSummaryData;
}

export const useQuoteSummaryHelper = (): UseQuoteSummaryHelperReturn => {
  const { quoteId } = useParams<{ quoteId: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);

  invariant(quoteId, 'quoteId is required');

  const { quote } = useQuote(quoteId);
  const { structuredQuote } = useQuoteStore((state) => ({ structuredQuote: state.structuredQuote }), shallow);
  const { data: policy } = useGetPolicy({ policyId: quote?.policyId });
  const { data: lastSuccessfulRating } = useSearchQuoteRatings({
    quoteId,
    enabled: true,
    params: searchLastSuccessfulRatingParams,
  });
  const { data: policyTransactions } = useSearchPolicyTransactions(
    { policyId: policy?.id ?? '' },
    [QuoteType.Renewal, QuoteType.Endorsement].includes(quote?.quoteType as QuoteType),
  );

  const isRatingModalOpen = searchParams.get(SEARCH_PARAMS[ModalType.RatingSummaryModal]) === 'true';
  const isTIVModalOpen = searchParams.get(SEARCH_PARAMS[ModalType.TIVModal]) === 'true';
  const isKYTaxModalOpen = searchParams.get(SEARCH_PARAMS[ModalType.KYTaxModal]) === 'true';

  const setModalOpen = (type: ModalType, isOpen: boolean) => {
    setSearchParams((params) => {
      const paramKey = SEARCH_PARAMS[type];
      if (isOpen) {
        params.set(paramKey, 'true');
      } else {
        params.delete(paramKey);
      }
      return params;
    });
  };

  const toggleModalOpen = (type: ModalType, isModalOpen: boolean) => setModalOpen(type, !isModalOpen);

  const summaryData = getQuoteSummaryData(
    quote as QuoteGetResponse,
    structuredQuote as StructuredQuote,
    policy as PolicyGetResponse,
    lastSuccessfulRating,
    policyTransactions || { items: [], pagination: { pageOffset: 0, pageLimit: 10, total: 0 } },
    isInternalUser,
    toggleModalOpen,
  );

  return {
    quoteId,
    quote,
    structuredQuote,
    policy,
    lastSuccessfulRating,
    policyTransactions,
    isInternalUser,
    isRatingModalOpen,
    isTIVModalOpen,
    isKYTaxModalOpen,
    toggleModalOpen,
    setModalOpen,
    summaryData,
  };
};
